import { irelandCountyOptions } from '@nxsys/constants/AppConstants';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import AppSelect from '@nxsys/components/AppSelect';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import {
  defaultCisStatus,
  defaultVatMethodOptions,
  yesNoOptions,
} from '@nxsys/constants/AppConstants';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Option } from '@nxsys/types/models/Common';
import { countryList } from '@nxsys/utils';
import { Col, Form, Row } from 'antd';
import { Moment } from 'moment';
import React, { Ref, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';
import {
  formatPostCode,
  postCodeValidationRule,
  vatRegNoValidationRule,
} from '@nxsys/utils/formFieldValidation';
import { useCompanyDetailFieldVisibility } from './useCompanyDetailFieldVisibility';

export type CompanyDetailFormValues = {
  companyName: string;
  tradingName: string;
  address1: string;
  address2: string;
  address3: string;
  address4: string;
  city: string;
  county: string;
  postCode: string;
  country: Option<string> | null;
  companyNo: string;
  incorporationDate: Moment | null;
  UTRNo: string;
  cisVerificationNo: string;
  cisVerificationDate: Moment | null;
  cisStatus: Option<string> | null;
  VATEffectiveDate: Moment | null;
  VATRegistered: boolean;
  VATMethod: Option<string> | null;
  VATRegNo: string;
  logoUrl: File | string | null;
  bannerUrl: File | string | null;
};

export type CompanyDetailFormRef = {
  submit: () => void;
};

type CompanyDetailFormProps = {
  formRef?: Ref<CompanyDetailFormRef>;
  onSubmit?: (values: CompanyDetailFormValues) => void;
  defaultValues?: Partial<CompanyDetailFormValues>;
  paymentModel?: string[];
};

const CompanyDetailForm: React.FC<CompanyDetailFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
  paymentModel,
}) => {
  const { messages, formatMessage } = useTypedIntl();

  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const { showCis, showVat, showVatMethod } =
    useCompanyDetailFieldVisibility(paymentModel);

  const [form] = Form.useForm<CompanyDetailFormValues>();
  const watchValues = Form.useWatch([], form);

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
  }));

  const onFinish = (values: CompanyDetailFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
      wrapperCol={{ span: 24 }}
      labelCol={{ span: 24 }}
      {...(defaultValues ? { initialValues: defaultValues } : {})}
    >
      <Row gutter={[16, 16]}>
        <Col sm={24} md={12}>
          <Form.Item
            label={
              isBureau
                ? messages['common.bureau.companyName']
                : messages['common.companyName']
            }
            name="companyName"
            rules={[
              {
                required: true,
                message: messages['form.pleaseEnter'],
              },
            ]}
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.tradingName']}
            name="tradingName"
            rules={[
              {
                required: true,
                message: messages['form.pleaseEnter'],
              },
            ]}
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={formatMessage(
              {
                id: 'common.addressLineWithNumber',
              },
              { number: 1 },
            )}
            name="address1"
            rules={[
              {
                required: true,
                message: messages['form.pleaseEnter'],
              },
            ]}
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={formatMessage(
              {
                id: 'common.addressLineWithNumber',
              },
              { number: 2 },
            )}
            name="address2"
            rules={[
              {
                required: true,
                message: messages['form.pleaseEnter'],
              },
            ]}
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={formatMessage(
              {
                id: 'common.addressLineWithNumber',
              },
              { number: 3 },
            )}
            name="address3"
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={formatMessage(
              {
                id: 'common.addressLineWithNumber',
              },
              { number: 4 },
            )}
            name="address4"
          >
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item label={messages['common.cityTown']} name="city">
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item label={messages['common.county']} name="county">
            {isIreland ? (
              <AppSelect
                showSearch
                options={irelandCountyOptions}
                placeholder={messages['form.select']}
              />
            ) : (
              <AppInput placeholder={messages['form.enter']} />
            )}
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={isIreland ? (messages['common.eirCode'] || 'EIR Code') : messages['common.postCode']}
            name="postCode"
            rules={[
              {
                required: true,
                message: messages['form.pleaseEnter'],
              },
              postCodeValidationRule(messages, payrollCountry),
            ]}
          >
            <AppInput
              placeholder={messages['form.enter']}
              upperCaseAll
              onChange={(e) => {
                form.setFieldValue(
                  'postCode',
                  formatPostCode(e.target.value, payrollCountry),
                );
              }}
              maxLength={8}
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item label={messages['common.country']} name="country">
            <AppSelect
              options={countryList}
              placeholder={messages['form.select']}
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item label={messages['common.companyNumber']} name="companyNo">
            <AppInput placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.incorporationDate']}
            name="incorporationDate"
          >
            <AppDatePicker
              placeholder={messages['form.select']}
              format="DD/MM/YYYY"
            />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={isIreland ? 'TRN Number' : messages['common.utrNumber']}
            name="UTRNo"
            rules={[
              {
                max: 13,
                message: isIreland
                  ? 'TRN Number must be shorter than or equal to 13 characters'
                  : 'UTR Number must be shorter than or equal to 13 characters',
              },
            ]}
          >
            <AppInput placeholder={messages['form.enter']} maxLength={13} />
          </Form.Item>
        </Col>
        {showCis && (
          <>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.cisVerificationNumber']}
                name="cisVerificationNo"
              >
                <AppInput placeholder={messages['form.enter']} />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item
                label={messages['common.cisVerificationDate']}
                name="cisVerificationDate"
              >
                <AppDatePicker
                  placeholder={messages['form.select']}
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col sm={24} md={12}>
              <Form.Item label={messages['common.cisStatus']} name="cisStatus">
                <AppSelect
                  options={defaultCisStatus}
                  placeholder={messages['form.select']}
                />
              </Form.Item>
            </Col>
          </>
        )}
        {showVat && (
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.vatEffectiveDate']}
              name="VATEffectiveDate"
            >
              <AppDatePicker
                placeholder={messages['form.select']}
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Col>
        )}
        {showVat && (
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.vatRegistered']}
              name="VATRegistered"
            >
              <AppRadioGroup options={yesNoOptions} />
            </Form.Item>
          </Col>
        )}
        {showVatMethod && (
          <Col sm={24} md={12}>
            <Form.Item label={messages['common.vatMethod']} name="VATMethod">
              <AppSelect
                options={defaultVatMethodOptions}
                placeholder={messages['form.select']}
              />
            </Form.Item>
          </Col>
        )}
        {showVat && (
          <Col sm={24} md={12}>
            <Form.Item
              label={messages['common.vatRegNumber']}
              name="VATRegNo"
              rules={[vatRegNoValidationRule(messages, payrollCountry)]}
              {...(isIreland && {
                normalize: (value: string) =>
                  value?.startsWith('IE') ? value : 'IE',
                initialValue: 'IE',
              })}
            >
              <AppInput placeholder={messages['form.enter']} maxLength={20} />
            </Form.Item>
          </Col>
        )}
        <Col span={24}>
          <Form.Item label={messages['common.attachLogo']} name="logoUrl">
            <AppUploadFile
              urlFile={
                typeof watchValues?.logoUrl === 'string'
                  ? watchValues?.logoUrl
                  : undefined
              }
              onChange={(file: File) => {
                form.setFieldValue('logoUrl', file);
              }}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item label={messages['common.attachBanner']} name="bannerUrl">
            <AppUploadFile
              urlFile={
                typeof watchValues?.bannerUrl === 'string'
                  ? watchValues?.bannerUrl
                  : undefined
              }
              onChange={(file: File) => {
                form.setFieldValue('bannerUrl', file);
              }}
            />
          </Form.Item>
        </Col>
      </Row>
    </AppFormContainer>
  );
};

export default CompanyDetailForm;
